import { useCallback } from 'react';

import Button from '@ingka/button';
import InlineMessage from '@ingka/inline-message';
import Modal, { ModalBody, ModalFooter, ModalHeader, Sheets } from '@ingka/modal';
import checkmarkIcon from '@ingka/ssr-icon/paths/checkmark';
import copyIcon from '@ingka/ssr-icon/paths/copy';
import informationCircleIcon from '@ingka/ssr-icon/paths/information-circle';
import { selectAssetSummaryArticles } from '@repo/api-adapters/selectors/cart.selectors';
import { selectAssetCreationDate, selectOrderType } from '@repo/api-adapters/selectors/customer-assets.selectors';
import { translate } from '@repo/phrase';
import type { AssetArticle, AssetType } from '@repo/types/asset';
import { formatDateTime } from '@repo/utils';

import { useAsset, useAssetParams } from '@/hooks/asset.hooks';
import { getAppConfig } from '@/utils/window.utils';

import { AssetSummaryHeader } from './asset-summary-header';
import { AssetSummaryModalArticleTable } from './asset-summary-modal-article-table';
import { useCopyAssetSummaryToClipboard } from './asset-summary-modal.hooks';

import styles from './styles.module.scss';

interface Props {
    visible: boolean;
    onClose: () => void;
}

interface AssetSummaryModalFooterProps {
    items: AssetArticle[];
    assetType: AssetType;
    createdAt?: string;
    orderType?: string | undefined;
    assetId: string;
    onClose: () => void;
}

const AssetSummaryModalFooter = ({
    items,
    assetType,
    createdAt,
    assetId,
    orderType,
    onClose,
}: AssetSummaryModalFooterProps) => {
    const { mutate, isPending, isSuccess } = useCopyAssetSummaryToClipboard({
        items,
        assetType,
        createdAt: createdAt ?? '-',
        assetId,
        orderType,
        onClose,
    });

    const copyToClipboard = useCallback(() => {
        mutate();
    }, [mutate]);

    return (
        <ModalFooter renderBorder>
            <div className={styles.sheets}>
                <Button
                    text={translate('asset_summary_modal_copy_to_clipboard')}
                    type="primary"
                    onClick={copyToClipboard}
                    disabled={isPending}
                    iconPosition="leading"
                    data-testid="asset-summary-modal-copy-to-clipboard"
                    ssrIcon={isSuccess ? checkmarkIcon : copyIcon}
                />
            </div>
        </ModalFooter>
    );
};

export const AssetSummaryModal = ({ visible, onClose }: Props) => {
    const { assetId, assetType } = useAssetParams();
    const { data } = useAsset(assetType, assetId);

    if (!data) {
        return null;
    }

    const orderType = selectOrderType(data);

    const createdAt = selectAssetCreationDate(data);
    const creationDate = createdAt ? formatDateTime(createdAt, getAppConfig().dateTimeFormat) : '-';
    const header = translate(`asset_summary_modal_header_${orderType ?? assetType}`);

    const items = selectAssetSummaryArticles(data);

    return (
        <Modal handleCloseBtn={onClose} visible={visible} data-testid="asset-summary-modal">
            <Sheets
                size="medium"
                aria-label={header}
                header={<ModalHeader title={header} />}
                footer={
                    <AssetSummaryModalFooter
                        items={items}
                        assetId={assetId}
                        assetType={assetType}
                        orderType={orderType}
                        createdAt={creationDate}
                        onClose={onClose}
                    />
                }
            >
                <ModalBody className={String(styles.modal_content)}>
                    <AssetSummaryHeader
                        assetId={assetId}
                        assetType={assetType}
                        orderType={orderType}
                        creationDate={creationDate}
                    />
                    <InlineMessage
                        data-testid="asset-summary-details-info-message"
                        className={String(styles.asset_summary_modal_details_message)}
                        body={translate('asset_summary_modal_details_informative_message')}
                        ssrIcon={informationCircleIcon}
                        variant="informative"
                        subtle
                    />
                    <AssetSummaryModalArticleTable items={items} assetType={assetType} />
                </ModalBody>
            </Sheets>
        </Modal>
    );
};
